import {
  Lock, Edit3, AlertTriangle, Camera, Download, RotateCcw, Loader2, ShieldCheck,
} from "lucide-react";
import { API } from "../../lib/api";
import { fmtDate } from "./utils";

// =====================================================================
// ICEditBanner — top banner shown when editing/viewing an existing IC.
// Shows submitted-by/created-at, locked indicator, Screenshot / Excel / New IC actions.
// Three visual states:
//   • locked (view-only for non-admins on Approved) → grey
//   • editing_approved (admin override on Approved) → emerald with shield
//   • normal editing → amber
// =====================================================================
export default function ICEditBanner({
  editing, meta, locked, shooting,
  onScreenshot, onResetForm,
}) {
  if (!editing) return null;
  const editingApproved = !!editing.editing_approved;
  const banner = locked
    ? { bg: "bg-zinc-100 border-zinc-300", icon: <Lock className="w-4 h-4 text-zinc-600 flex-shrink-0" />, title: "Viewing approved IC" }
    : editingApproved
      ? { bg: "bg-emerald-50 border-emerald-300", icon: <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0" />, title: "Editing Approved IC · admin override" }
      : { bg: "bg-amber-50 border-amber-300", icon: <Edit3 className="w-4 h-4 text-amber-700 flex-shrink-0" />, title: "Editing IC" };
  return (
    <div
      className={`rounded-xl border-2 px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-sm ${banner.bg}`}
      data-testid="ic-edit-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        {banner.icon}
        <div className="min-w-0">
          <div className="font-display font-bold text-[13px] text-zinc-900 truncate">
            {banner.title}: {meta.property_name}
          </div>
          <div className="text-[10.5px] text-zinc-600 truncate">
            Submitted by {editing.submitted_by_name || "—"} · {fmtDate(editing.created_at)} · status <b>{editing.decision}</b>
            {locked && " · locked (only admin can override)"}
            {editingApproved && " · saving will overwrite the approved record"}
          </div>
          {editing.legacy_estimates && !locked && (
            <div className="text-[10px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 mt-1 inline-flex items-center gap-1" data-testid="ic-edit-legacy-notice">
              <AlertTriangle className="w-2.5 h-2.5" />
              Older IC — individual BD entries not saved; rows pre-filled with the averaged value
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <button
          onClick={onScreenshot}
          disabled={shooting}
          data-testid="ic-edit-banner-screenshot"
          className="text-[11px] text-zinc-700 px-2.5 py-1.5 rounded border border-zinc-300 hover:bg-zinc-50 disabled:opacity-60 inline-flex items-center gap-1 font-medium pointer-events-auto"
          title="Capture the IC as a PNG to share on WhatsApp"
        >
          {shooting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Screenshot
        </button>
        <a
          href={`${API}/property-ic/submissions/${editing.id}/export`}
          target="_blank"
          rel="noreferrer"
          data-testid="ic-edit-banner-export"
          className="text-[11px] text-zinc-700 px-2.5 py-1.5 rounded border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-1 font-medium"
        >
          <Download className="w-3 h-3" /> Excel
        </a>
        <button
          onClick={onResetForm}
          data-testid="ic-edit-banner-new"
          className="text-[11px] font-medium text-zinc-900 px-2.5 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-1"
        >
          <RotateCcw className="w-3 h-3" /> New IC
        </button>
      </div>
    </div>
  );
}
